import { ComponentFactoryResolver, Injectable, Injector } from "@angular/core";
import { MassUploadEntryConfig, MassUploadStateFormMap, EXCEL_PERSON_TYPES, EXCEL_ENROLLMENT_TYPES } from "../interfaces/mass-upload-grid.interface";
import { LoadFormComponentService } from "@ptl/forms";
import { MASS_UPLOAD_STATE_CUSTOM, MASS_UPLOAD_STATE_NAME, MASS_UPLOAD_STATE_SUBSCRIBER } from "../reducer/mass-upload-grid.reducer";


@Injectable({
  providedIn: 'root'
})
export class MassUploadGridDataHelperService {

  gridFormMap: MassUploadStateFormMap[] = [];


  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private loadFormComponentService: LoadFormComponentService,
    private injector: Injector,
  ) {
    
  }

  async createAndInitializeComponents(formJson: any, entryList: MassUploadEntryConfig[], formGroup) {
    const componentInstances: any[] = [];

    // Create component instances for all entries in this row
    for (const entry of entryList) {
      if (entry.formControlAttributes?.formControlName) {
        try {
          //const entryComponent = this.loadEntryComponentService.getEntryComponent(entry);
          //const componentFactory = this.componentFactoryResolver.resolveComponentFactory(MassUploadFormControlComponent);
          
          const entryComponent = this.loadFormComponentService.getEntryComponent(entry);
          const componentFactory = this.componentFactoryResolver.resolveComponentFactory(
            entryComponent
          );
          
          //const viewContainerRef = this.injector.get(ViewContainerRef);
          const componentRef = componentFactory.create(this.injector);
          
          // Create the component instance
          //const componentRef = this.hiddenContainer.createComponent(componentFactory);
          
          // Set the required properties on the component instance
          (componentRef.instance as any).formConfig = formJson;
          (componentRef.instance as any).entry = entry;
          (componentRef.instance as any).parentForm = formGroup;
          (componentRef.instance as any).reviewOnly = false;
          
          if (entry.formControlAttributes?.connectPath) {
            (componentRef.instance as any).form = formGroup[entry.formControlAttributes.connectPath];
          }
          
          componentInstances.push(componentRef);
        } 
        catch (error) {
          console.warn(`Failed to create component for entry ${entry.formControlAttributes?.formControlName}:`, error);
        }
      }
    }

    // Run ngOnInit for all component instances
    for (const componentRef of componentInstances) {
      if (componentRef.instance.ngOnInit) {
        componentRef.instance.ngOnInit();
      }
    }

    // Run ngAfterViewInit for all component instances
    for (const componentRef of componentInstances) {
      if (componentRef.instance.ngAfterViewInit) {
        componentRef.instance.ngAfterViewInit();
      }
    }

    return componentInstances;
  }

  /**
   * 
   * @param stateId Any state Id of any data row
   * 
   * @description Will find the owning subscriber for any given data row stateId.
   * 
   * @returns The subscriber that owns this stateId, or the subscriber the matches this stateId
   */
  getOwnerFormMapFromStateId(stateId: number): MassUploadStateFormMap {
    let gridIndex = -1;

    // First, find the correct entry based on the stateId in gridFormMap. This will be our anchor
    for (let i = 0; i < this.gridFormMap.length; i++) {
      if (this.gridFormMap[i].stateId === stateId) {
        gridIndex = i;
        break;
      }
    }

    // This will occur when we are removing a row and we attempt to refresh a subscriber that is no longer there
    if (gridIndex === -1) {
      return null;
    }

    return this.getOwnerFormMapFromGridIndex(gridIndex);
  }

  getOwnerFormMapFromGridIndex(gridIndex: number) {
    // traverse backwards (upwards) through the data until we hit a subscriber or child-only row
    for (let i = gridIndex; i >= 0; i--) {
      let personType = this.getPersonTypeByStateId(this.gridFormMap[i].stateId);
      if (personType === EXCEL_PERSON_TYPES.SUBSCRIBER || personType === EXCEL_PERSON_TYPES.CHILD_ONLY) {
        return this.gridFormMap[i];
      }
    }
    
    return null;
  }

  getFullFamilyFormGroupsBySubscriberStateId(subscriberStateId: number): MassUploadStateFormMap[] {
    let gridIndex = -1;

    // First, find the correct entry based on the stateId in gridFormMap. This will be our Subscriber
    // Then, use the index of that entry to interate over the next N rows to gather the dependents' formGroups
    for (let i = 0; i < this.gridFormMap.length; i++) {
      if (this.gridFormMap[i].stateId === subscriberStateId) {
        gridIndex = i;
        break;
      }
    }

    // This shouldn't ever happen so if you see this error your code is calling this method with a non existant stateId
    if (gridIndex === -1) {
      console.error("Cannot build dependent list. No entry found for stateId: " + subscriberStateId);
      return [];
    }

    return this.getFullFamilyFormGroupsBySubscriberGridIndex(gridIndex);
  }

  getFullFamilyFormGroupsBySubscriberGridIndex(gridIndex: number) {
    let resultFormGroups: MassUploadStateFormMap[] = [];

    resultFormGroups.push(this.gridFormMap[gridIndex]);
    for (let i = gridIndex+1; i < this.gridFormMap.length; i++) {
      let stateId = this.gridFormMap[i].stateId;
      let personType = this.getPersonTypeByStateId(stateId);

      if (personType === EXCEL_PERSON_TYPES.DEPENDENT || personType === EXCEL_PERSON_TYPES.SPOUSE) {
        resultFormGroups.push(this.gridFormMap[i]);
      }
      else {
        break;
      }
    }

    return resultFormGroups;
  }

  isValidFormGroup(gridFormMapEntry: MassUploadStateFormMap) {
    const statusValue = this.getStatusByStateId(gridFormMapEntry.stateId);
    if (statusValue) {
      return false;
    }

    return true;
  }

  /**
   * COUNTY NAME
   */
  getCountyNameByStateId(stateId: number): string {
    let gridFormEntry = this.gridFormMap.find((formMap) => formMap.stateId === stateId);
    return gridFormEntry.formGroups[this.getCountyFormKey(stateId)].controls.county.value;
  }
  getCountyFormKey(stateId: number): string {
    return `${MASS_UPLOAD_STATE_NAME}.${stateId}.${MASS_UPLOAD_STATE_SUBSCRIBER}`;
  }

  /**
   * ZIP CODE
   */
  getZipCodeByStateId(stateId: number): string {
    let gridFormEntry = this.gridFormMap.find((formMap) => formMap.stateId === stateId);
    return gridFormEntry.formGroups[this.getZipCodeFormKey(stateId)].controls.zipCode.value?.toString();
  }
  getZipCodeFormKey(stateId: number): string {
    return `${MASS_UPLOAD_STATE_NAME}.${stateId}.${MASS_UPLOAD_STATE_SUBSCRIBER}`;
  }

  /**
   * PERSON TYPE
   */
  getPersonTypeByStateId(stateId: number): EXCEL_PERSON_TYPES {
    let gridFormEntry = this.gridFormMap.find((formMap) => formMap.stateId === stateId);
    return gridFormEntry.formGroups[this.getPersonTypeFormKey(stateId)].controls.personType.value;
  }
  getPersonTypeFormKey(stateId: number): string {
    return `${MASS_UPLOAD_STATE_NAME}.${stateId}.${MASS_UPLOAD_STATE_CUSTOM}`;
  }


  /**
   * ENROLLMENT TYPE
   */
  getEnrollmentTypeByStateId(stateId: number): EXCEL_ENROLLMENT_TYPES {
    let gridFormEntry = this.gridFormMap.find((formMap) => formMap.stateId === stateId);
    return gridFormEntry.formGroups[this.getEnrollmentTypeFormKey(stateId)].controls.enrollmentType.value;
  }
  getEnrollmentTypeFormKey(stateId: number): string {
    return `${MASS_UPLOAD_STATE_NAME}.${stateId}.${MASS_UPLOAD_STATE_CUSTOM}`;
  }


  /**
   * EFFECTIVE DATE
   */
  getEffectiveDateByStateId(stateId: number): EXCEL_ENROLLMENT_TYPES {
    let gridFormEntry = this.gridFormMap.find((formMap) => formMap.stateId === stateId);
    return gridFormEntry.formGroups[this.getEffectiveDateFormKey(stateId)].controls.effectiveDate.value;
  }
  getEffectiveDateFormKey(stateId: number): string {
    return `${MASS_UPLOAD_STATE_NAME}.${stateId}.${MASS_UPLOAD_STATE_CUSTOM}`;
  }


  /**
   * PLAN REGION
   */
  getPlanRegionByStateId(stateId: number): string {
    let gridFormEntry = this.gridFormMap.find((formMap) => formMap.stateId === stateId);
    return gridFormEntry.formGroups[this.getPlanRegionFormKey(stateId)].controls.planRegion.value;
  }
  getPlanRegionFormKey(stateId: number): string {
    return `${MASS_UPLOAD_STATE_NAME}.${stateId}.${MASS_UPLOAD_STATE_CUSTOM}`;
  }


  /**
   * ERROR STATUS OF ROW
   */
  getStatusByStateId(stateId: number): string {
    let gridFormEntry = this.gridFormMap.find((formMap) => formMap.stateId === stateId);
    return gridFormEntry.formGroups[this.getStatusFormKey(stateId)].controls.statusColumn.value;
  }
  getStatusFormKey(stateId: number): string {
    return `${MASS_UPLOAD_STATE_NAME}.${stateId}.${MASS_UPLOAD_STATE_CUSTOM}`;
  }


  /**
   * SUBSCRIBER INFO KEY
   */
  getSubscriberFormKey(stateId: number): string {
    return `${MASS_UPLOAD_STATE_NAME}.${stateId}.${MASS_UPLOAD_STATE_SUBSCRIBER}`;
  }
}